// CTSK-514 — pure helpers for the /notes wall: column count, JS masonry
// placement, resize clamping and the "where did it land" maths for a drop.
// No React in here so every rule is unit-tested without a DOM.

import { NOTE_HEIGHT_MAX, NOTE_HEIGHT_MIN, type NoteGridType } from '@captain-tasko/shared';

export const WALL_GAP = 14;
/** The wall fills its page up to this width; cards keep ~357 px at three across. */
export const WALL_MAX_WIDTH = 1100;
export const LIST_MAX_WIDTH = 720;

/** 3 across on wide screens, 2 on medium, 1 on narrow; List is always 1. */
export function columnCount(containerWidth: number, grid: NoteGridType): number {
  if (grid === 'list') return 1;
  if (containerWidth >= 1000) return 3;
  if (containerWidth >= 640) return 2;
  return 1;
}

export function columnWidth(containerWidth: number, cols: number, gap = WALL_GAP): number {
  return Math.max(0, Math.floor((containerWidth - gap * (cols - 1)) / cols));
}

export interface Placement {
  col: number;
  x: number;
  y: number;
}

/**
 * Left-to-right masonry: each item, in order, goes into the currently shortest
 * column (ties go to the leftmost). `heights` are the measured card heights.
 */
export function masonryLayout(
  heights: readonly number[],
  cols: number,
  colWidth: number,
  gap = WALL_GAP,
): { placements: Placement[]; height: number } {
  const colH: number[] = Array.from({ length: Math.max(1, cols) }, () => 0);
  const placements: Placement[] = [];
  for (const h of heights) {
    let c = 0;
    for (let i = 1; i < colH.length; i++) if (colH[i]! < colH[c]!) c = i;
    placements.push({ col: c, x: c * (colWidth + gap), y: colH[c]! });
    colH[c]! += h + gap;
  }
  const tallest = Math.max(0, ...colH);
  return { placements, height: heights.length > 0 ? Math.max(0, tallest - gap) : 0 };
}

export const clampHeight = (px: number): number =>
  Math.min(NOTE_HEIGHT_MAX, Math.max(NOTE_HEIGHT_MIN, Math.round(px)));

/**
 * A drop of `activeId` on `overId` → the API anchor. Dragging forward lands the
 * note AFTER the one it was dropped on, dragging back lands it BEFORE (the same
 * result as dnd-kit's arrayMove), so what the user sees is what is saved.
 */
export function dropTarget(
  ids: readonly string[],
  activeId: string,
  overId: string,
): { beforeId: string } | { afterId: string } | null {
  if (activeId === overId) return null;
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from < 0 || to < 0) return null;
  return from < to ? { afterId: overId } : { beforeId: overId };
}

/** `arrayMove` for note lists, by id; returns the same array when nothing moves. */
export function moveById<T extends { id: string }>(list: readonly T[], activeId: string, overId: string): T[] {
  const from = list.findIndex((n) => n.id === activeId);
  const to = list.findIndex((n) => n.id === overId);
  if (from < 0 || to < 0 || from === to) return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** Put `id` right before/after `anchorId`; same array contents otherwise. */
export function moveRelative<T extends { id: string }>(list: readonly T[], id: string, anchorId: string, where: 'before' | 'after'): T[] {
  if (id === anchorId || !list.some((n) => n.id === id) || !list.some((n) => n.id === anchorId)) return [...list];
  const rest = list.filter((n) => n.id !== id);
  const moved = list.find((n) => n.id === id)!;
  const at = rest.findIndex((n) => n.id === anchorId);
  rest.splice(where === 'before' ? at : at + 1, 0, moved);
  return rest;
}

/** Split `flat` back into pages of the given sizes (the last page takes the remainder). */
export function rechunk<T>(flat: readonly T[], sizes: readonly number[]): T[][] {
  const out: T[][] = [];
  let at = 0;
  sizes.forEach((size, i) => {
    const end = i === sizes.length - 1 ? flat.length : at + size;
    out.push(flat.slice(at, end));
    at = end;
  });
  return out;
}
